import axios, { AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios'
import {
    BillingBillableDeliveryNoteSearchGridRequest,
    BillingCaseSearchGridRequest,
    BillingPatientSearchGridRequest
} from '@/api/services/billing/billing.dto'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { InvoicePatchRequest,
    InvoiceDeliveryNoteAddDetachRequest,
    InvoiceResponse
} from '@/api/services/billing/common-invoice/billing-common-invoice.dto'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { CommonFilterModel, CommonParams, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { ReportGridRequest } from '@/api/services/common.dto'
import { Nullable } from '@/shared/types/app.types'

class BillingCommonInvoiceService {
    async paginator(
        filterModel: Nullable<CommonFilterModel>,
        params: Nullable<CommonParams>,
        laboratoryId: number
    ): Promise<IResponseAgGrid<InvoiceResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoiceResponse>>(
                `/billing/laboratories/${laboratoryId}/invoices`,
                { ...filterModel },
                { params })
        return response.data
    }
    async export(
        laboratoryId: number,
        gridColumns?: ReportGridRequest,
        params?: ExportCommonParams
    ): Promise<AxiosResponse<Blob>> {
        return axios.put<Blob>(
            `/billing/laboratories/${laboratoryId}/invoices/export`,
            { ...gridColumns },
            { params, responseType: 'arraybuffer' })
    }

    async searchPatient(
        laboratoryId: number,
        request: BillingPatientSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<InvoiceResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoiceResponse>>(
                `/billing/laboratories/${laboratoryId}/invoices/patient`,
                request,
                { params })
        return response.data
    }
    async searchCase(
        laboratoryId: number,
        request: BillingCaseSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<InvoiceResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoiceResponse>>(
                `/billing/laboratories/${laboratoryId}/invoices/case`,
                request,
                { params })
        return response.data
    }
    async searchBillableDeliveryNote(
        laboratoryId: number,
        request: BillingBillableDeliveryNoteSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<InvoiceResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoiceResponse>>(
                `/billing/laboratories/${laboratoryId}/invoices/billableDeliveryNote`,
                request,
                { params })
        return response.data
    }

    async get(
        laboratoryId: number,
        invoiceId: number,
        cancelToken?: CancelToken
    ): Promise<InvoiceResponse> {
        const response =
            await axios.get<InvoiceResponse>(
                `/billing/laboratories/${laboratoryId}/invoices/${invoiceId}`,
                { cancelToken })
        return response.data
    }

    async verify(
        laboratoryId: number,
        invoiceId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoices/${invoiceId}/verify`)
        return response.data
    }
    async detachDeliveryNotes(
        laboratoryId: number,
        invoiceId: number,
        request: InvoiceDeliveryNoteAddDetachRequest
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoices/${invoiceId}/detachDeliveryNotes`,
                request)
        return response.data
    }
    async addDeliveryNotes(
        laboratoryId: number,
        invoiceId: number,
        request: InvoiceDeliveryNoteAddDetachRequest
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoices/${invoiceId}/addDeliveryNotes`,
                request)
        return response.data
    }
    async cancel(
        laboratoryId: number,
        invoiceId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoices/${invoiceId}/cancel`)
        return response.data
    }

    async create(
        laboratoryId: number,
        deliveryNoteIds: number[]
    ): Promise<InvoiceResponse> {
        const response =
            await axios.post<InvoiceResponse>(
                `/billing/laboratories/${laboratoryId}/invoices`,
                { deliveryNoteIds })
        return response.data
    }

    async update(
        laboratoryId: number,
        invoicePatchRequest: InvoicePatchRequest
    ): Promise<EntityIdentifierWithErrorResponse[]> {
        const response =
            await axios.patch<EntityIdentifierWithErrorResponse[]>(
                `/billing/laboratories/${laboratoryId}/invoices`,
                invoicePatchRequest)
        return response.data
    }

    async getReport(
        laboratoryId: number,
        invoiceId: number,
        encoded: boolean
    ): Promise<Blob> {
        const config: AxiosRequestConfig = {
            params: { encoded },
            headers: { type: 'application/pdf' },
            responseType: encoded ? 'text' : 'arraybuffer'
        }
        const response =
            await axios.put<Blob>(
                `/billing/laboratories/${laboratoryId}/invoices/${invoiceId}/report`,
                undefined,
                config)
        return response.data
    }
    async replaceReport(
        laboratoryId: number,
        invoiceId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoices/${invoiceId}/replaceReport`)
        return response.data
    }
}
export const billingCommonInvoiceService = new BillingCommonInvoiceService()
